// Recording playback and download in the call detail (UX spec 6.5, FE-15 subset). Shown only when
// the server says playback is enabled for this person and the recording is available; every other
// case is an exact state, never a player that can't play.
import type { Capability, Recording } from '@bcio-voice/contracts';
import { type KeyboardEvent, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { Download, Pause, Play, RotateCcw, RotateCw } from 'lucide-react';
import { Icon } from '../../ui/Icon.tsx';
import { Dialog } from '../../ui/Dialog.tsx';
import { StatePanel } from '../../ui/StatePanel.tsx';
import { Diagnostics } from '../../ui/Diagnostics.tsx';
import { useWorkspace } from '../../state/workspace.tsx';
import { useDesktop } from '../../desktop/bridge.ts';
import { useAnnouncer } from '../../ui/Announcer.tsx';
import { gate } from '../../lib/capabilities.ts';
import { gateDiagnostic } from '../../lib/userCopy.ts';
import { formatAbsolute, formatDuration } from '../../lib/format.ts';
import {
  type ArchiveCopy, type ArchiveFailure, DESKTOP_PLAYBACK, PLAYBACK_NOT_SET_UP, PLAYBACK_RATES, RecordingPlayback,
  type RecordingWithProvenance, SKIP_SECONDS, archiveCopy, describeRecordingGap, exportRecording, mediaPath, probeMedia, scrubTarget,
} from './archive.ts';

/** What the recording area shows for one recording, decided before any media request. */
export type PlaybackAvailability =
  | { kind: 'player' }
  | { kind: 'desktop' }
  | { kind: 'not_set_up'; reason: 'unconfigured' | 'disabled' | 'unimplemented' }
  | { kind: 'not_allowed' }
  | { kind: 'checking' }
  | { kind: 'not_ready' };

export function playbackAvailability(recording: Pick<Recording, 'available'>, playback: Capability | null, desktop: boolean): PlaybackAvailability {
  if (desktop) return { kind: 'desktop' };
  const g = gate(playback);
  if (g.render === 'explain') return { kind: 'not_set_up', reason: g.reason };
  if (g.render === 'hidden') return g.reason === 'not_allowed' ? { kind: 'not_allowed' } : { kind: 'checking' };
  if (!recording.available) return { kind: 'not_ready' };
  return { kind: 'player' };
}

/** Retention line from the Recording contract (UX 6.5). The access reason isn't in the contract yet. */
export function retentionLine(recording: Pick<Recording, 'retention_until'>, timeZone?: string): string {
  return recording.retention_until ? `Retained until ${formatAbsolute(recording.retention_until, timeZone)}.` : 'No deletion date is set for this recording.';
}

function ArchiveState({ copy, failure, onRetry }: { copy: ArchiveCopy; failure?: ArchiveFailure | null; onRetry?: () => void }) {
  return (
    <StatePanel
      variant={copy.variant}
      compact
      headingLevel={3}
      title={copy.title}
      body={copy.body}
      action={copy.retry && onRetry ? <button type="button" className="btn" onClick={onRetry}>Try again</button> : undefined}
      details={failure ? { code: failure.code, requestId: failure.requestId } : null}
    />
  );
}

export function RecordingProvenanceDetails({ recording }: { recording: RecordingWithProvenance }) {
  const segments = recording.segments ?? [];
  const gaps = recording.gaps ?? [];
  const segCount = recording.segment_count ?? (segments.length || 1);
  const overlapTrimmed = recording.overlap_trimmed_ms ?? 0;
  if (segCount <= 1 && gaps.length === 0 && overlapTrimmed === 0) return null;
  return (
    <details className="diagnostics">
      <summary>Technical details ({segCount} source segment{segCount === 1 ? '' : 's'})</summary>
      <div className="diagnostics-body">
        {recording.continuity_note ? <p className="meta">{recording.continuity_note}</p> : null}
        {overlapTrimmed > 0 ? <p className="meta">Overlap trimmed across segment boundaries: {formatDuration(overlapTrimmed)}</p> : null}
        {segments.length > 0 ? (
          <ul className="plain-list" aria-label="Source recording segments">
            {segments.map(s => (
              <li key={s.id} className="meta num">
                Part {s.index}: {formatDuration(s.start_ms)}–{formatDuration(s.end_ms)} ({formatDuration(s.duration_ms)}, samples {s.start_sample}–{s.end_sample}, policy rev {s.policy_revision}, {s.status}{s.trimmed_overlap_ms > 0 ? `, trimmed ${formatDuration(s.trimmed_overlap_ms)} overlap` : ''})
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </details>
  );
}

export function RecordingMedia({ recording, playback }: { recording: RecordingWithProvenance; playback: Capability | null }) {
  const ws = useWorkspace();
  const desktop = !!useDesktop();
  const availability = playbackAvailability(recording, playback, desktop);
  switch (availability.kind) {
    case 'desktop':
      return <ArchiveState copy={DESKTOP_PLAYBACK} />;
    case 'not_set_up':
      return (
        <>
          <ArchiveState copy={availability.reason === 'unconfigured' ? PLAYBACK_NOT_SET_UP : { ...PLAYBACK_NOT_SET_UP, title: availability.reason === 'disabled' ? 'Playback is turned off for this workspace' : 'Playback isn’t available in this version' }} />
          <Diagnostics diagnostic={gateDiagnostic(gate(playback), playback?.reason_code ?? null)} />
        </>
      );
    case 'not_allowed':
      return <ArchiveState copy={archiveCopy('denied')} />;
    case 'checking':
      return <p className="field-hint" role="status">Checking whether recordings can be played here…</p>;
    case 'not_ready':
      return (
        <>
          {recording.status === 'failed' ? (
            <p className="field-hint">This recording can’t be played because a recording segment failed.</p>
          ) : recording.status === 'partial' ? (
            <p className="field-hint">This recording can’t be played because one or more segments are unavailable or no longer retained.</p>
          ) : (
            <p className="field-hint">This recording can’t be played yet. It’s still being saved to the archive.</p>
          )}
          <RecordingProvenanceDetails recording={recording} />
        </>
      );
    default:
      return (
        <>
          <RecordingPlayer key={recording.id} recording={recording} />
          {recording.download_allowed ? <ExportButton recording={recording} /> : <p className="field-hint">Downloading isn’t allowed for this recording.</p>}
          <RecordingProvenanceDetails recording={recording} />
          {gate(playback).render === 'degraded' ? <p className="field-hint">Playback is having problems right now. If it doesn’t start, try again later.</p> : null}
          {ws.config.value?.data_mode === 'fixture' ? <p className="notice notice-info">Fixture data: not a real recording.</p> : null}
        </>
      );
  }
}

/** Keyboard-operable player for one recording. The audio element has no native controls. */
export function RecordingPlayer({ recording }: { recording: RecordingWithProvenance }) {
  const ws = useWorkspace();
  const audioRef = useRef<HTMLAudioElement>(null);
  const [player] = useState(() => new RecordingPlayback({
    url: ws.api.url(mediaPath(recording.id)),
    probe: () => probeMedia(ws.api, recording.id),
    durationHintMs: recording.duration_ms,
  }));
  const snap = useSyncExternalStore(player.subscribe, player.getSnapshot, player.getSnapshot);
  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    // Detaching pauses, removes the source and its listeners (also under StrictMode remounts).
    return player.attach(el);
  }, [player]);

  const labelId = `rec-${recording.id}-label`;
  const duration = snap.duration;
  const gaps = recording.gaps ?? [];
  const timeText = `${formatDuration(snap.position * 1000)} of ${duration === null ? 'unknown length' : formatDuration(duration * 1000)}`;
  // Play stays available while loading: some browsers (iOS Safari) load nothing until play is pressed.
  const busy = snap.status === 'retrying';
  const onScrubKey = (e: KeyboardEvent<HTMLInputElement>) => {
    const target = scrubTarget(e.key, snap.position, duration);
    if (target === null) return;
    e.preventDefault();
    player.seekTo(target);
  };

  return (
    <div className="player" role="group" aria-labelledby={labelId}>
      <span id={labelId} className="visually-hidden">Recording player</span>
      {/* The media route streams with the same-origin session cookie; no native controls. */}
      <audio ref={audioRef} preload="metadata" />
      {snap.status === 'problem' && snap.failure ? (
        <ArchiveState copy={archiveCopy(snap.failure.problem)} failure={snap.failure} onRetry={() => player.retry()} />
      ) : (
        <>
          <div className="player-controls">
            <button type="button" className="btn btn-icon" onClick={() => player.seekBy(-SKIP_SECONDS)} aria-label="Back 10 seconds" disabled={busy}>
              <Icon icon={RotateCcw} />
            </button>
            <button type="button" className="btn btn-icon" onClick={() => player.toggle()} aria-label={snap.playing ? 'Pause' : 'Play'} disabled={busy}>
              <Icon icon={snap.playing ? Pause : Play} />
            </button>
            <button type="button" className="btn btn-icon" onClick={() => player.seekBy(SKIP_SECONDS)} aria-label="Forward 10 seconds" disabled={busy}>
              <Icon icon={RotateCw} />
            </button>
            <span className="player-time num" aria-hidden="true">{formatDuration(snap.position * 1000)} / {duration === null ? '--:--' : formatDuration(duration * 1000)}</span>
            <label className="player-rate">
              <span className="visually-hidden">Playback speed</span>
              <select value={String(snap.rate)} onChange={e => player.setRate(Number(e.target.value))}>
                {PLAYBACK_RATES.map(r => <option key={r} value={String(r)}>{r}x speed</option>)}
              </select>
            </label>
          </div>
          <input
            type="range"
            className="player-scrub"
            aria-label="Position in recording"
            aria-valuetext={timeText}
            min={0}
            max={Math.max(1, Math.floor(duration ?? 0))}
            step={1}
            value={Math.floor(snap.position)}
            onChange={e => player.seekTo(Number(e.target.value))}
            onKeyDown={onScrubKey}
            disabled={busy || duration === null}
          />
          {gaps.length > 0 ? (
            <ul className="plain-list recording-gaps" aria-label="Unrecorded spans in this call">
              {gaps.map((g, idx) => (
                <li key={`${g.after_segment_id}-${g.before_segment_id}-${idx}`} className="field-hint">
                  {describeRecordingGap(g)}
                </li>
              ))}
            </ul>
          ) : null}
          <p className="field-hint" role="status">{snap.status === 'loading' ? 'Loading recording…' : snap.status === 'retrying' ? 'The recording stopped loading. Checking with the server…' : ''}</p>
        </>
      )}
    </div>
  );
}

/** Download through the audited export route. The server's `download_allowed` gates the button. */
export function ExportButton({ recording }: { recording: Recording }) {
  const ws = useWorkspace();
  const { announce } = useAnnouncer();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<ArchiveFailure | null>(null);
  const titleId = `export-${recording.id}-title`;
  const run = async () => {
    setBusy(true);
    setFailure(null);
    const result = await exportRecording(ws.api, recording.id);
    setBusy(false);
    if (result) {
      setFailure(result);
      return;
    }
    setOpen(false);
    announce('Download started');
  };
  const copy = failure ? archiveCopy(failure.problem) : null;
  return (
    <>
      <button type="button" className="btn btn-small" onClick={() => { setFailure(null); setOpen(true); }}>
        <Icon icon={Download} /> Download
      </button>
      <Dialog open={open} title="Download recording" labelledBy={titleId} onClose={() => { if (!busy) setOpen(false); }}
        footer={(
          <>
            <button type="button" className="btn btn-primary" onClick={() => void run()} disabled={busy} aria-busy={busy || undefined}>{busy ? 'Preparing download…' : 'Download'}</button>
            <button type="button" className="btn btn-quiet" onClick={() => setOpen(false)} disabled={busy}>Cancel</button>
          </>
        )}>
        <p>Downloads are logged. The file is a copy of this call’s audio; keep it where only people allowed to hear it can reach it.</p>
        {copy && failure ? (
          <p className="field-error" role="alert">{copy.title}. {copy.body}{failure.requestId ? ` Reference ${failure.requestId}.` : ''}</p>
        ) : null}
      </Dialog>
    </>
  );
}
